import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  type ComponentType,
} from 'react';
import { Box, Fade, Stack, Typography } from '@mui/material';
import type { HomeSectionKey } from '@/constants/analytics';
import type { AnalyticsItemClick } from '@/context/analytics-item/interfaces';
import { AnalyticsItemClickProvider } from '@/context/analytics-item/provider';
import {
  CardsSkeleton,
  PPSkeleton,
  ProfileSkeleton,
} from '@/packages/home/components/skeleton';
import { useHomeAnalytics } from '@/packages/home/context/home-analytics';
import { useIaBuildContext } from '@/packages/home/context/ia-builder';

export type SkeletonType = 'cards' | 'profile' | 'pp';

interface SectionProps {
  children: ReactNode;
  description?: ReactNode;
  title?: ReactNode;
  tag?: ComponentType;
  skeletonType?: SkeletonType;
  onContentComplete?: () => void;
  id: number;
  sectionKey: HomeSectionKey;
  skipIaBuilder?: boolean;
}

interface ContentProps {
  description: ReactNode;
  title: ReactNode;
  tag: ReactNode;
  children: ReactNode;
}

const VIEWED_RATIO = 0.2;

const Content = ({ description, title, tag, children }: ContentProps) => {
  return (
    <Stack gap={description ? 1 : 2}>
      <Stack
        direction="row"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={{ xs: 1, md: 2 }}
      >
        {title && <Typography variant="h2">{title}</Typography>}
        {tag}
      </Stack>
      {description && <Typography mb={1}>{description}</Typography>}
      <Box>{children}</Box>
    </Stack>
  );
};

export const Section = ({
  children,
  description,
  title,
  tag,
  skeletonType,
  onContentComplete,
  id,
  sectionKey,
  skipIaBuilder = false,
}: SectionProps) => {
  const { visibleSectionIds, showAllSections, isAllSectionsLoaded } =
    useIaBuildContext();
  const homeAnalytics = useHomeAnalytics();
  const TagElement = tag;
  const isVisible = skipIaBuilder
    ? showAllSections || isAllSectionsLoaded
    : visibleSectionIds.includes(id);

  const hasTriggered = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const position = id + 1;

  useEffect(() => {
    const element = sectionRef.current;
    if (!isVisible || !element || !homeAnalytics) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // The first callback fires on observe even below the threshold.
        if (!entry?.isIntersecting || entry.intersectionRatio < VIEWED_RATIO)
          return;

        homeAnalytics.markSectionViewed(sectionKey, position);
        observer.disconnect();
      },
      { threshold: VIEWED_RATIO }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isVisible, homeAnalytics, sectionKey, position]);

  const handleItemClick = useCallback(
    (click: AnalyticsItemClick) =>
      homeAnalytics?.trackItemClick(sectionKey, position, click),
    [homeAnalytics, sectionKey, position]
  );

  const renderSkeleton = () => {
    const skeletons = {
      profile: <ProfileSkeleton />,
      pp: <PPSkeleton />,
      cards: <CardsSkeleton />,
    };

    return skeletons[skeletonType || 'cards'];
  };

  if (!isVisible) {
    if (showAllSections) {
      return renderSkeleton();
    }
    return null;
  }

  return (
    <AnalyticsItemClickProvider onItemClick={handleItemClick}>
      <Box id={`section-${id}`} ref={sectionRef}>
        {showAllSections ? (
          <Content
            description={description}
            title={title}
            tag={TagElement ? <TagElement /> : undefined}
            children={children}
          />
        ) : (
          <Fade
            in={true}
            timeout={800}
            onEntered={() => {
              if (onContentComplete && !hasTriggered.current) {
                hasTriggered.current = true;
                setTimeout(() => onContentComplete(), 200);
              }
            }}
          >
            <Box>
              <Content
                description={description}
                title={title}
                tag={TagElement ? <TagElement /> : undefined}
                children={children}
              />
            </Box>
          </Fade>
        )}
      </Box>
    </AnalyticsItemClickProvider>
  );
};
